import { Img } from 'react-image';
import { createStyles, makeStyles, type Theme } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { Box } from '@material-ui/core';
import Button, { ButtonVariant } from 'components/UI/V2/Button/Button';

const useStyles = makeStyles<
    Theme,
    { primaryButton?: { color?: string }; marginRightDescription?: string; videoMarginTop?: string }
>((theme: Theme) =>
    createStyles({
        slide: {
            flex: '0 0 100%',
            minWidth: 0,
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: '#f8f6f2',
            display: 'flex',
            [theme.breakpoints.up('md')]: {
                height: '588px'
            }
        },
        slideImage: {
            flex: '0 0 45%',
            position: 'relative',
            [theme.breakpoints.down('sm')]: {
                display: 'none'
            }
        },
        slideContent: {
            flex: '0 0 55%',
            padding: theme.spacing(4),
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.down('sm')]: {
                flex: '0 0 100%'
            }
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.down('sm')]: {
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(2),
                marginBottom: theme.spacing(2)
            }
        },
        stepNumber: {
            fontSize: 22,
            fontWeight: 500,
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            backgroundColor: '#000',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            [theme.breakpoints.down('sm')]: {
                fontSize: 16,
                width: '38px',
                minWidth: '38px',
                height: '38px',
                marginBottom: 0
            }
        },
        heading: {
            fontSize: 36,
            fontWeight: 600,
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(2),
            lineHeight: 1.2,
            textAlign: 'left',
            marginRight: theme.spacing(2),
            [theme.breakpoints.down('sm')]: {
                fontSize: 18,
                marginBottom: 0,
                flexShrink: 1,
                marginTop: 0
            }
        },
        description: {
            fontSize: 16,
            fontWeight: 400,
            marginRight: props => props.marginRightDescription || '69px',
            marginTop: '0px',
            textAlign: 'left',
            [theme.breakpoints.down('sm')]: {
                marginRight: '0px !important'
            }
        },
        videoPreview: {
            backgroundColor: '#fff',
            borderRadius: '12px',
            alignItems: 'center',
            marginTop: props => props.videoMarginTop || 'auto',
            display: 'flex',
            gap: theme.spacing(3),
            marginRight: '60px',
            cursor: 'pointer',
            [theme.breakpoints.down('sm')]: {
                marginRight: 0
            }
        },
        videoThumbnail: {
            width: '145px',
            height: '95px',
            borderRadius: '8px',
            overflow: 'hidden',
            position: 'relative',
            border: '1px solid #ddd',
            backgroundColor: '#ddd'
        },
        playButton: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: '#f8971d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            paddingLeft: '3px'
        },
        videoInfo: {
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'left'
        },
        videoTitle: {
            fontWeight: 600,
            marginBottom: theme.spacing(0.5),
            paddingLeft: '10px'
        },
        videoDuration: {
            fontSize: '12px',
            color: '#666',
            paddingTop: '10px'
        },
        buttonsContainer: {
            display: 'flex',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(3)
        },
        primaryButton: {
            'backgroundColor': props => props.primaryButton?.color || '#f8971d',
            'color': '#fff',
            'padding': '8px 20px',
            'borderRadius': '31px',
            'fontWeight': 600,
            'fontSize': 14,
            'border': 'none',
            'cursor': 'pointer',
            'textDecoration': 'none',
            'alignItems': 'center',
            'display': 'flex',
            'height': '40px',
            '&:hover': {
                backgroundColor: props => props.primaryButton?.color || '#C18733'
            }
        },
        secondaryButton: {
            'backgroundColor': 'transparent',
            'textDecoration': 'underline',
            'color': '#333',
            'borderRadius': '20px',
            'fontWeight': 600,
            'border': '0px solid #ccc',
            'cursor': 'pointer',
            'fontSize': '14px',
            '&:hover': {
                backgroundColor: 'transparent !important',
                color: '#C18733'
            }
        }
    })
);

export interface Slide {
    id: number;
    stepNumber?: number;
    title: string;
    description: string | React.ReactNode;
    marginRightDescription?: string;
    videoMarginTop?: string;
    image: string;
    videoThumbnail?: string;
    videoTitle?: string;
    videoDuration?: string;
    videoUrl?: string;
    primaryButton?: {
        text: string;
        href?: string;
        target?: string;
        rel?: string;
        color?: string;
        colorVariant?: ButtonVariant;
        hoverColor?: string;
        disabled?: boolean;
        onClick?: () => void;
    };
    secondaryButton?: {
        text: string;
        href?: string;
        target?: string;
        rel?: string;
        color?: string;
        onClick?: () => void;
    };
}

interface SlideProps {
    slide: Slide;
    onVideoClick: () => void;
}

export default function Slide({ slide, onVideoClick }: SlideProps) {
    const classes = useStyles({
        primaryButton: slide.primaryButton,
        marginRightDescription: slide.marginRightDescription,
        videoMarginTop: slide.videoMarginTop
    });

    return (
        <Box
            className={classes.slide}
            key={slide.id}
        >
            <Box className={classes.slideImage}>
                <Img
                    src={slide.image}
                    alt={slide.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    loader={<Box style={{ width: '100%', height: '100%', backgroundColor: '#eee' }} />}
                />
            </Box>
            <Box className={classes.slideContent}>
                {(slide.stepNumber || slide.title) && (
                    <Box className={classes.headerContainer}>
                        {slide.stepNumber && <Box className={classes.stepNumber}>{slide.stepNumber}</Box>}
                        {slide.title && <h2 className={classes.heading}>{slide.title}</h2>}
                    </Box>
                )}

                {slide.description && typeof slide.description === 'string' ? (
                    <p
                        className={classes.description}
                        dangerouslySetInnerHTML={{
                            __html: slide.description
                        }}
                    ></p>
                ) : (
                    <>{slide.description}</>
                )}

                {(slide.primaryButton || slide.secondaryButton) && (
                    <Box className={classes.buttonsContainer}>
                        {slide.primaryButton &&
                            (slide.primaryButton.href ? (
                                <a
                                    className={classes.primaryButton}
                                    href={slide.primaryButton.href}
                                    target={slide.primaryButton.target}
                                    rel={slide.primaryButton.rel}
                                >
                                    {slide.primaryButton.text}
                                </a>
                            ) : (
                                <Button
                                    label={slide.primaryButton.text}
                                    onClick={slide.primaryButton.onClick}
                                    variant={slide.primaryButton?.colorVariant}
                                    disabled={slide.primaryButton?.disabled}
                                />
                            ))}
                        {slide.secondaryButton &&
                            (slide.secondaryButton.href ? (
                                <a
                                    className={classes.secondaryButton}
                                    href={slide.secondaryButton.href}
                                    target={slide.secondaryButton.target}
                                    rel={slide.secondaryButton.rel}
                                >
                                    {slide.secondaryButton.text}
                                </a>
                            ) : (
                                <Button
                                    className={classes.secondaryButton}
                                    label={slide.secondaryButton.text}
                                    onClick={slide.secondaryButton.onClick}
                                    color={slide.secondaryButton.color}
                                />
                            ))}
                    </Box>
                )}

                {slide.videoUrl && slide.videoThumbnail && (
                    <Box
                        className={classes.videoPreview}
                        onClick={onVideoClick}
                    >
                        <Box className={classes.videoThumbnail}>
                            <Img
                                src={slide.videoThumbnail}
                                alt={slide.videoTitle}
                                style={{ width: '90%', height: '90%', paddingTop: '10px', paddingLeft: '10px' }}
                                loader={<Box style={{ width: '100%', height: '100%', backgroundColor: '#eee' }} />}
                            />
                            <Box className={classes.playButton}>
                                <FontAwesomeIcon
                                    icon={faPlay}
                                    size="lg"
                                    color="#222222"
                                />
                            </Box>
                        </Box>
                        <Box className={classes.videoInfo}>
                            <Box>
                                <FontAwesomeIcon
                                    icon={faVideo}
                                    size="lg"
                                    color="#5E8677"
                                />
                                <span className={classes.videoTitle}>{slide.videoTitle}</span>
                            </Box>
                            <span className={classes.videoDuration}>Duration: {slide.videoDuration}</span>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
